import { FC, useState } from "react";

import { useAv } from "../../../../utils/av";
import {
  DEFAULT_BREAKOUT_DURATION,
  MIN_GROUP_COUNT,
  useBreakouts,
} from "../../../../utils/breakouts";
import { Link } from "../../../../utils/routing";
import { pageUrl } from "../../../../utils/urls";

import { PageType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { Card } from "mds/components/Card";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { RefreshIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectActiveClassSession, selectPageById } from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";

interface GroupConfigBreakoutsBannerProps {
  page: PageType;
  hasAssessments: boolean;
}

export const GroupConfigBreakoutsBanner: FC<GroupConfigBreakoutsBannerProps> = ({
  page,
  hasAssessments,
}) => {
  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const { onStartBreakouts } = useBreakouts();

  const [shouldSyncBreakoutsWithPageGroups, setShouldSyncBreakoutsWithPageGroups] =
    useState(!hasAssessments);
  const classSession = useAppSelector(selectActiveClassSession);

  const syncedPage = useAppSelector((s) =>
    selectPageById(s, classSession?.breakout_session_synced_page_id),
  );

  const onStartBreakoutsFromGroupConfig = () => {
    onStartBreakouts(
      "page",
      DEFAULT_BREAKOUT_DURATION,
      page.id,
      MIN_GROUP_COUNT,
      shouldSyncBreakoutsWithPageGroups,
    );
  };

  if (
    !classSession ||
    !isInVirtualClass ||
    !isVirtualClassActive ||
    (isInBreakout && !classSession.breakout_session_synced_page_id)
  )
    return null;

  if (isInBreakout) {
    if (classSession?.breakout_session_synced_page_id === page.id) {
      return (
        <Card className="flex w-full flex-wrap items-center justify-between">
          <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
            <RefreshIcon />
            {t("group_configuration_card.breakouts_synced_with_this_page")}
          </div>
          <Tooltip text={t("group_configuration_card.breakouts_synced_with_this_page_tooltip")} />
        </Card>
      );
    }

    if (classSession.breakout_session_synced_page_id && !!syncedPage) {
      return (
        <Card className="flex w-full items-center justify-between">
          <div className="flex items-center gap-1 truncate text-sm font-medium text-black-tint-20">
            <RefreshIcon />
            <Trans
              i18nKey="group_configuration_card.breakouts_synced_with_another_page"
              t={t}
              values={{ title: syncedPage.title }}
            >
              <Link
                className="truncate text-blue"
                to={pageUrl(syncedPage.section_id, syncedPage.id)}
              >
                {syncedPage.title}
              </Link>
            </Trans>
          </div>
          <Tooltip
            text={t("group_configuration_card.breakouts_synced_with_another_page_tooltip")}
          />
        </Card>
      );
    }
  }

  if (!isInBreakout) {
    return (
      <Card className="flex w-full flex-wrap items-center justify-between">
        <Tooltip
          text={
            hasAssessments
              ? t("group_configuration_card.breakouts_synced_disabled_tooltip")
              : undefined
          }
        >
          <ToggleSwitch
            disabled={hasAssessments}
            isSelected={shouldSyncBreakoutsWithPageGroups}
            labelStart={
              <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
                <RefreshIcon />
                {t("group_configuration_card.keep_groups_and_breakouts_in_sync")}
              </div>
            }
            onClick={() => setShouldSyncBreakoutsWithPageGroups(!shouldSyncBreakoutsWithPageGroups)}
          />
        </Tooltip>

        <Button kind="blue" size="s" onClick={onStartBreakoutsFromGroupConfig}>
          {t("breakout_group_modal.start_breakouts")}
        </Button>
      </Card>
    );
  }

  return null;
};
